import { useEffect } from "react";
import {
  subscribeAuthSessionInvalid,
  type AuthSessionInvalidCode,
} from "@/shared/auth/authSessionEvents";
import { useAlertDialog } from "@/shared/hooks/useSystemUI";
import { accountStatusPopup } from "@/features/auth/ui/account-status/accountStatusPopup";
import { DIALOG_PRIORITY } from "@/shared/model/feedbackStore";
import { useAuthNavigation } from "./useAuthNavigation";

/**
 * 계정 상태 코드(USER_WITHDRAWN·USER_SUSPENDED·USER_BLOCKED)는 accountStatusPopup 빌더로 분기하므로
 * 문구 맵에서 제외한다. 나머지 세션 무효 코드만 여기서 [제목, 본문]으로 안내한다.
 * REFRESH_TOKEN_REUSED 는 단순 만료가 아니라 보안 사고 시그널이므로 별도 문구.
 * 두 문장 본문은 \n 줄바꿈 (AlertDialog message 는 white-space: pre-line).
 */
type NonAccountStatusCode = Exclude<
  AuthSessionInvalidCode,
  "USER_WITHDRAWN" | "USER_SUSPENDED" | "USER_BLOCKED"
>;

const POPUP_BY_CODE: Record<NonAccountStatusCode, readonly [string, string]> = {
  REFRESH_TOKEN_REUSED: ["보안 알림", "로그인 정보가 변경되었습니다.\n다시 로그인해주세요."],
  REFRESH_TOKEN_EXPIRED: ["로그인 안내", "로그인이 만료되었습니다.\n다시 로그인해주세요."],
  REFRESH_TOKEN_INVALID: ["로그인 안내", "로그인이 만료되었습니다.\n다시 로그인해주세요."],
  AUTH_TOKEN_MISSING: ["로그인 안내", "로그인이 필요합니다."],
  UNAUTHORIZED: ["로그인 안내", "로그인이 만료되었습니다.\n다시 로그인해주세요."],
  SESSION_EXPIRED: ["로그인 안내", "로그인이 만료되었습니다.\n다시 로그인해주세요."],
};

/**
 * 세션 무효 이벤트(refreshCoordinator·인증 apiClient 발신)를 구독해
 * 로그아웃 네비게이션과 안내 팝업을 처리한다.
 *
 * shared/auth 는 router/UI 를 모르므로, 사용자 대면 복구는 features 계층인 이 훅이 담당한다.
 * INVALID_REQUEST / 서버 / 네트워크 / timeout 은 세션 무효 이벤트로 오지 않으므로 강제 로그인 팝업이 뜨지 않는다.
 *
 * 우선순위: 세션 팝업은 DIALOG_PRIORITY.SESSION(2)으로 열어 일반 에러 팝업(0)에 덮이지 않게 한다.
 * - USER_WITHDRAWN·USER_SUSPENDED·USER_BLOCKED: accountStatusPopup 빌더 → forbidden 아이콘 + 안내(SESSION 내장).
 *   탈퇴/정지는 detail 기반 동적, 차단은 고정 카피(detail 미노출).
 * - AUTH_TOKEN_MISSING: 클라이언트 판정 fallback(1) — 지연 도착 시 서버 확정 안내(2)를 격하하지 못함.
 * - 그 외(REFRESH_TOKEN_*·UNAUTHORIZED): 서버 확정(2).
 */
export const useAuthSessionRecovery = (): void => {
  const { logOut } = useAuthNavigation();
  const { open: openErrorPopup } = useAlertDialog();

  useEffect(
    () =>
      subscribeAuthSessionInvalid(({ errorCode, detail }) => {
        logOut();

        // 탈퇴/정지/차단 — forbidden 아이콘 + 안내 빌더 (SESSION 우선순위 내장)
        if (
          errorCode === "USER_WITHDRAWN" ||
          errorCode === "USER_SUSPENDED" ||
          errorCode === "USER_BLOCKED"
        ) {
          openErrorPopup(accountStatusPopup(errorCode, detail));
          return;
        }

        const [title, message] = POPUP_BY_CODE[errorCode];
        openErrorPopup({
          title,
          message,
          priority:
            errorCode === "AUTH_TOKEN_MISSING"
              ? DIALOG_PRIORITY.SESSION_FALLBACK
              : DIALOG_PRIORITY.SESSION,
        });
      }),
    [logOut, openErrorPopup],
  );
};
